import React, { useState } from 'react';
import styled from 'styled-components';
import { motion, AnimatePresence } from 'framer-motion';
import { FileText, MessageSquare, LineChart, PenTool, Tags, Globe, Wrench, Mic, Network, Search, HelpCircle, Smartphone, Edit3, MessageCircle, Zap, ChevronDown } from 'lucide-react';
import solutionsBg from '../assets/images/solutions-bg.png';

const SolutionsSection = styled.section`
  padding-top: 12rem;
  background-color: var(--bg-primary);
  min-height: 100vh;
  position: relative;
  overflow: hidden;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background-image: url(${solutionsBg});
    background-size: cover;
    background-position: center 20%;
    background-attachment: fixed;
    opacity: 0.15;
    z-index: 0;
    mix-blend-mode: color-dodge;
  }
`;

const BackgroundGlow = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 500px;
  background: radial-gradient(ellipse at top, rgba(59, 130, 246, 0.15), transparent 70%);
  z-index: 0;
  pointer-events: none;
`;

const SolutionsGridContainer = styled.div`
  max-width: 1000px;
  margin: 4rem auto;
  position: relative;
  z-index: 1;
`;

const SolutionCard = styled(motion.div)`
  background: rgba(255, 255, 255, 0.02);
  border: 1px solid var(--border-subtle);
  border-radius: 16px;
  margin-bottom: 2rem;
  overflow: hidden;
  will-change: transform, height, opacity;

  &:hover {
    background: rgba(255, 255, 255, 0.03);
    border-color: rgba(59, 130, 246, 0.3);
    box-shadow: 0 10px 40px -10px rgba(59, 130, 246, 0.1);
  }
`;

const SolutionHeader = styled.div`
  padding: 2rem 2.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  cursor: pointer;
  user-select: none;

  @media (max-width: 768px) {
    padding: 1.5rem;
  }
`;

const HeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 1.5rem;

  .icon-wrapper {
    width: 60px;
    height: 60px;
    border-radius: 14px;
    background: linear-gradient(135deg, rgba(59, 130, 246, 0.15), rgba(139, 92, 246, 0.15));
    color: var(--accent-primary);
    display: flex;
    align-items: center;
    justify-content: center;
    flex-shrink: 0;
  }

  .title-group {
    h3 {
      color: var(--text-primary);
      font-size: 1.5rem;
      font-weight: 600;
      margin-bottom: 0.25rem;
      letter-spacing: -0.01em;
    }

    p {
      color: var(--text-secondary);
      font-size: 1rem;
      margin: 0;
    }
  }
`;

const ExpandIcon = styled(motion.div)`
  color: var(--text-tertiary);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const SolutionDetails = styled(motion.div)`
  padding: 0 2.5rem 2.5rem 8.5rem;
  
  @media (max-width: 768px) {
    padding: 0 1.5rem 1.5rem 1.5rem;
  }

  .details-grid {
    display: grid;
    grid-template-columns: 1fr 1fr;
    gap: 2rem;
    padding-top: 1.5rem;
    border-top: 1px solid var(--border-subtle);

    @media (max-width: 768px) {
      grid-template-columns: 1fr;
    }
  }

  .detail-section {
    h4 {
      color: var(--accent-primary);
      font-size: 1rem;
      font-weight: 600;
      margin-bottom: 1rem;
      text-transform: uppercase;
      letter-spacing: 0.05em;
    }

    ul {
      list-style: none;
      
      li {
        color: var(--text-secondary);
        position: relative;
        padding-left: 1.5rem;
        margin-bottom: 0.8rem;
        line-height: 1.5;

        &::before {
          content: '→';
          position: absolute;
          left: 0;
          color: var(--accent-secondary);
        }
      }
    }
  }
`;

const Solutions = () => {
  const [expandedIndex, setExpandedIndex] = useState(null);

  const toggleAccordion = (index) => {
    setExpandedIndex(expandedIndex === index ? null : index);
  };

  const solutions = [
    {
      icon: <FileText size={32} />,
      title: 'Intelligent Document Processing',
      description: 'Transform unstructured documents into structured, actionable data.',
      features: [
        'Automated data extraction',
        'Invoice and receipt processing',
        'Contract analysis and management',
        'Form recognition and processing'
      ],
      useCases: [
        'Finance & Accounting automation',
        'Legal document review',
        'Healthcare records management'
      ]
    },
    {
      icon: <MessageSquare size={32} />,
      title: 'Conversational AI',
      description: 'Next-generation chatbots and virtual assistants that understand context.',
      features: [
        'Natural Language Understanding (NLU)',
        'Context-aware responses',
        'Multi-turn conversations',
        'Seamless human handoff'
      ],
      useCases: [
        '24/7 Customer support',
        'Internal IT helpdesk',
        'Automated lead qualification'
      ]
    },
    {
      icon: <LineChart size={32} />,
      title: 'Customer Insights',
      description: 'Extract deep, actionable insights from customer interactions and feedback.',
      features: [
        'Sentiment analysis',
        'Intent recognition',
        'Trend forecasting',
        'Topic modeling'
      ],
      useCases: [
        'Brand monitoring',
        'Product feedback analysis',
        'Customer satisfaction tracking'
      ]
    },
    {
      icon: <PenTool size={32} />,
      title: 'Content Generation',
      description: 'Automate the creation of high-quality, contextual content at scale.',
      features: [
        'Automated report generation',
        'Personalized email drafting',
        'Product description creation',
        'Template-based natural language generation'
      ],
      useCases: [
        'E-commerce catalog management',
        'Marketing copy generation',
        'Financial reporting'
      ]
    },
    {
      icon: <Tags size={32} />,
      title: 'Text Classification',
      description: 'Automatically route, tag, and organize large volumes of text data.',
      features: [
        'Multi-label classification',
        'Custom taxonomy support',
        'High-accuracy routing rules',
        'Continuous learning models'
      ],
      useCases: [
        'Support ticket routing',
        'Email triage',
        'News aggregation'
      ]
    },
    {
      icon: <Globe size={32} />,
      title: 'Multilingual NLP',
      description: 'Break geographical boundaries with seamless cross-lingual processing.',
      features: [
        'Real-time translation',
        'Cross-lingual sentiment analysis',
        'Language identification',
        'Multilingual search'
      ],
      useCases: [
        'Global customer support',
        'International market research',
        'Localization workflows'
      ]
    },
    {
      icon: <Wrench size={32} />,
      title: 'Custom Model Development',
      description: 'Bespoke AI architectures designed for your unique operational data.',
      features: [
        'Domain-specific fine-tuning',
        'Proprietary data training',
        'Model optimization and pruning',
        'Secure deployment pipelines'
      ],
      useCases: [
        'Niche industry terminology processing',
        'Highly specialized classification tasks',
        'Proprietary predictive analytics'
      ]
    }
  ];

  return (
    <SolutionsSection>
      <BackgroundGlow />
      <div className="container">
        <motion.div
          style={{ textAlign: 'center', marginBottom: '4rem', position: 'relative', zIndex: 1 }}
          initial={{ opacity: 0, y: 30 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="section-subtitle">Core Capabilities</h2>
          <h1 className="section-title">Enterprise Intelligence <span className="gradient-text-accent">Arsenal</span></h1>
          <p className="section-description" style={{ margin: '0 auto', fontSize: '1.2rem', maxWidth: '800px' }}>
            We deploy highly specialized, scalable intelligence pipelines engineered to automate
            complexity and uncover hidden value within your organization's data.
          </p>
        </motion.div>

        <SolutionsGridContainer>
          {solutions.map((solution, index) => {
            const isExpanded = expandedIndex === index;

            return (
              <SolutionCard
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true, margin: "-50px" }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
              >
                <SolutionHeader onClick={() => toggleAccordion(index)}>
                  <HeaderContent>
                    <div className="icon-wrapper">{solution.icon}</div>
                    <div className="title-group">
                      <h3>{solution.title}</h3>
                      <p>{solution.description}</p>
                    </div>
                  </HeaderContent>
                  <ExpandIcon
                    animate={{ rotate: isExpanded ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                  >
                    <ChevronDown size={24} />
                  </ExpandIcon>
                </SolutionHeader>

                <AnimatePresence initial={false}>
                  {isExpanded && (
                    <SolutionDetails
                      initial="collapsed"
                      animate="open"
                      exit="collapsed"
                      variants={{
                        open: { opacity: 1, height: "auto", marginTop: 0 },
                        collapsed: { opacity: 0, height: 0, marginTop: 0 }
                      }}
                      transition={{ duration: 0.4, ease: [0.04, 0.62, 0.23, 0.98] }}
                    >
                      <div className="details-grid">
                        <div className="detail-section">
                          <h4>Key Features</h4>
                          <ul>
                            {solution.features.map((feature, idx) => (
                              <li key={idx}>{feature}</li>
                            ))}
                          </ul>
                        </div>
                        <div className="detail-section">
                          <h4>Primary Use Cases</h4>
                          <ul>
                            {solution.useCases.map((useCase, idx) => (
                              <li key={idx}>{useCase}</li>
                            ))}
                          </ul>
                        </div>
                      </div>
                    </SolutionDetails>
                  )}
                </AnimatePresence>
              </SolutionCard>
            );
          })}
        </SolutionsGridContainer>
      </div>
    </SolutionsSection>
  );
};

export default Solutions;